import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, useForm, useFormState } from 'react-final-form';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import SearchBarSingle from '../../../../../components/SearchBarV3/SearchBarSingle';
import { BLACK } from '../../../../../theme/colorConstants';
import { minutesFromMidnightToTime } from '../../../../../utils';
import { weekDays as DAYS_OF_WEEK_FULL } from '../../../../../utils/timeConverter';
import CadenceField from '../../../CreateJobCard/CustomComponents/CadenceField';
import { getAllValidShiftTimes } from '../../../helpers';
import {
  calculateExpectedHoursInWeek,
  convertToJobOrders,
  detectOverlaps,
} from '../../utils/recurringTwoWeeksHelpers';
import { getSundayOfWeek } from '../confirmModalUtils';
import StandardErrorBox from '../CustomComponents/StandardErrorBox';

import getStyles from './RecurringTwoWeeksSelector.styles';

// Mapping between day names (used by CadenceField) and day indices (used by shifts)
const DAY_NAME_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

const DAY_INDEX_TO_NAME = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

// Data format for CadenceField
const DAYS_OF_WEEK_DATA = [
  { label: 'Sun', value: 'sunday' },
  { label: 'Mon', value: 'monday' },
  { label: 'Tue', value: 'tuesday' },
  { label: 'Wed', value: 'wednesday' },
  { label: 'Thu', value: 'thursday' },
  { label: 'Fri', value: 'friday' },
  { label: 'Sat', value: 'saturday' },
];

const RecurringTwoWeeksSelector = ({ wrapperSx }) => {
  const form = useForm();
  const formState = useFormState();
  const theme = useTheme();

  const currentFormValues = formState.values;
  const { positionTemplate } = currentFormValues;
  const dateRange = currentFormValues?.dateRange || [null, null];
  const [startDate] = dateRange;

  const [week1StartDate, setWeek1StartDate] = useState(null);
  const [week2StartDate, setWeek2StartDate] = useState(null);

  // Calculate week start dates based on the user-selected start date (from dateRange).
  // This keeps local validations (jobOrders, overlaps, OT) consistent with what we submit.
  useEffect(() => {
    if (!startDate) {
      setWeek1StartDate(null);
      setWeek2StartDate(null);
      return;
    }

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const week1Start = getSundayOfWeek(start);
    const week2Start = new Date(week1Start);
    week2Start.setDate(week1Start.getDate() + 7);

    setWeek1StartDate(week1Start);
    setWeek2StartDate(week2Start);
  }, [startDate]);

  // State for each week: { dayOfWeek: { startTime: minutes, endTime: minutes, openings: number } }
  const [week1Shifts, setWeek1Shifts] = useState({});
  const [week2Shifts, setWeek2Shifts] = useState({});
  const [activeWeek, setActiveWeek] = useState(1);

  const minutesInDay = 24 * 60;
  const maxShiftDurationMinutes = 20 * 60;

  const getShiftDurationMinutes = useCallback(
    ({ startTime, endTime }) => {
      if (startTime == null || endTime == null) return null;

      if (endTime >= startTime) return endTime - startTime;

      // Overnight shift
      return minutesInDay - startTime + endTime;
    },
    [minutesInDay]
  );

  const isInvalidShiftDuration = useCallback(
    ({ startTime, endTime }) => {
      const duration = getShiftDurationMinutes({ startTime, endTime });
      if (duration == null) return false;
      return duration > maxShiftDurationMinutes || duration === 0;
    },
    [getShiftDurationMinutes, maxShiftDurationMinutes]
  );

  // Track per-week invalid shift durations so we can highlight bad rows and block submit.
  const invalidShiftDaysWeek1 = useMemo(() => {
    const invalidDays = new Set();
    Object.keys(week1Shifts || {}).forEach((dayIndexStr) => {
      const shift = week1Shifts?.[dayIndexStr];
      const dayIndex = parseInt(dayIndexStr, 10);
      if (!shift || Number.isNaN(dayIndex)) return;
      if (isInvalidShiftDuration({ startTime: shift.startTime, endTime: shift.endTime })) {
        invalidDays.add(dayIndex);
      }
    });
    return invalidDays;
  }, [week1Shifts, isInvalidShiftDuration]);

  const invalidShiftDaysWeek2 = useMemo(() => {
    const invalidDays = new Set();
    Object.keys(week2Shifts || {}).forEach((dayIndexStr) => {
      const shift = week2Shifts?.[dayIndexStr];
      const dayIndex = parseInt(dayIndexStr, 10);
      if (!shift || Number.isNaN(dayIndex)) return;
      if (isInvalidShiftDuration({ startTime: shift.startTime, endTime: shift.endTime })) {
        invalidDays.add(dayIndex);
      }
    });
    return invalidDays;
  }, [week2Shifts, isInvalidShiftDuration]);

  const hasInvalidShiftDuration = useMemo(
    () => invalidShiftDaysWeek1.size > 0 || invalidShiftDaysWeek2.size > 0,
    [invalidShiftDaysWeek1, invalidShiftDaysWeek2]
  );

  const currentWeekShifts = activeWeek === 1 ? week1Shifts : week2Shifts;
  const setCurrentWeekShifts = activeWeek === 1 ? setWeek1Shifts : setWeek2Shifts;

  // Keep form values in sync with local component state + selected dateRange.
  // This avoids stale jobOrders when the user changes the date range after selecting shifts.
  useEffect(() => {
    const positionTemplateId = positionTemplate?._id;

    form.batch(() => {
      form.change('week1Shifts', week1Shifts);
      form.change('week2Shifts', week2Shifts);
      form.change('hasInvalidShiftDuration', hasInvalidShiftDuration);

      if (!positionTemplateId || !week1StartDate || !week2StartDate) {
        form.change('jobOrders', []);
        form.change('hasOverlap', false);
        form.change('isValid', !hasInvalidShiftDuration);
        return;
      }

      const jobOrders = convertToJobOrders(
        week1Shifts,
        week2Shifts,
        week1StartDate,
        week2StartDate,
        positionTemplateId
      );
      const overlapCheck = detectOverlaps(jobOrders);

      form.change('jobOrders', jobOrders);
      form.change('hasOverlap', overlapCheck.hasOverlap);
      form.change('isValid', !overlapCheck.hasOverlap && !hasInvalidShiftDuration);
    });
  }, [
    form,
    positionTemplate,
    week1Shifts,
    week2Shifts,
    week1StartDate,
    week2StartDate,
    hasInvalidShiftDuration,
  ]);

  // Convert currentWeekShifts to daysOfWeek format for CadenceField
  const daysOfWeekValue = useMemo(() => {
    const daysOfWeek = {};
    Object.keys(currentWeekShifts).forEach((dayIndex) => {
      const dayName = DAY_INDEX_TO_NAME[parseInt(dayIndex, 10)];
      if (dayName) {
        daysOfWeek[dayName] = true;
      }
    });
    return daysOfWeek;
  }, [currentWeekShifts]);

  // Handle day selection change from CadenceField
  const handleDaysOfWeekChange = useCallback(
    (newDaysOfWeek) => {
      const newShifts = { ...currentWeekShifts };

      // Remove days that were deselected
      Object.keys(newDaysOfWeek).forEach((dayName) => {
        if (!newDaysOfWeek[dayName]) {
          const dayIndex = DAY_NAME_TO_INDEX[dayName];
          if (dayIndex !== undefined && newShifts[dayIndex]) {
            delete newShifts[dayIndex];
          }
        }
      });

      // Add days that were selected (without default times)
      Object.keys(newDaysOfWeek).forEach((dayName) => {
        if (newDaysOfWeek[dayName]) {
          const dayIndex = DAY_NAME_TO_INDEX[dayName];
          if (dayIndex !== undefined && !newShifts[dayIndex]) {
            newShifts[dayIndex] = {
              startTime: null,
              endTime: null,
              openings: 1,
            };
          }
        }
      });

      setCurrentWeekShifts(newShifts);
    },
    [currentWeekShifts, setCurrentWeekShifts]
  );

  // Get selected days for current week
  const selectedDays = useMemo(
    () => Object.keys(currentWeekShifts).filter((day) => currentWeekShifts[day]),
    [currentWeekShifts]
  );

  // Convert shifts to list format for display
  const shiftsList = useMemo(
    () =>
      selectedDays
        .map((dayIndex) => {
          const shift = currentWeekShifts[dayIndex];
          if (!shift) return null;

          return {
            dayIndex: parseInt(dayIndex, 10),
            dayName: DAYS_OF_WEEK_FULL[dayIndex],
            startTime: shift.startTime != null ? shift.startTime : null,
            endTime: shift.endTime != null ? shift.endTime : null,
            openings: shift.openings ?? 1,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.dayIndex - b.dayIndex),
    [selectedDays, currentWeekShifts]
  );

  // Calculate week total openings
  const weekTotalOpenings = useMemo(
    () => shiftsList.reduce((total, shift) => total + (shift.openings || 0), 0),
    [shiftsList]
  );

  // Calculate expected hours for overtime detection
  const expectedHours = useMemo(() => {
    if (!week1StartDate || !week2StartDate) return 0;

    const currentStartDate = activeWeek === 1 ? week1StartDate : week2StartDate;
    const jobs = shiftsList
      .filter((shift) => shift.startTime != null && shift.endTime != null) // TODO: will be revisited when date selector is implemented
      .map((shift) => {
        const shiftDate = new Date(currentStartDate);
        // Set to the correct day of week (0 = Sunday, 1 = Monday, etc.)
        const currentDay = shiftDate.getDay();
        // Handle wrap-around: if dayIndex < currentDay, add 7 to get next occurrence
        // e.g., Monday (1) to Sunday (0): (0 - 1 + 7) % 7 = 6 days forward
        const daysToAdd = (shift.dayIndex - currentDay + 7) % 7;
        shiftDate.setDate(shiftDate.getDate() + daysToAdd);

        // Create start and end times
        const startTime = minutesFromMidnightToTime(shift.startTime);
        const endTime = minutesFromMidnightToTime(shift.endTime);

        const startDateTime = new Date(shiftDate);
        startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

        const endDateTime = new Date(shiftDate);
        endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

        // Handle overnight shifts
        if (endDateTime < startDateTime) {
          endDateTime.setDate(endDateTime.getDate() + 1);
        }

        return {
          startEpochMs: startDateTime.getTime(),
          endEpochMs: endDateTime.getTime(),
        };
      });

    return calculateExpectedHoursInWeek(jobs);
  }, [shiftsList, activeWeek, week1StartDate, week2StartDate]);

  const hasOvertime = expectedHours > 40;

  // Detect overlaps across both weeks
  const overlapDetection = useMemo(() => {
    if (!week1StartDate || !week2StartDate || !positionTemplate?._id) {
      return { hasOverlap: false, overlappingPairs: [] };
    }

    const allJobOrders = convertToJobOrders(
      week1Shifts,
      week2Shifts,
      week1StartDate,
      week2StartDate,
      positionTemplate._id
    );

    return detectOverlaps(allJobOrders);
  }, [week1Shifts, week2Shifts, week1StartDate, week2StartDate, positionTemplate]);

  // Handle shift time change (accepts object from SearchBarSingle with { value: minutes } format)
  const handleTimeChange = useCallback(
    (dayIndex, timeType, value) => {
      // Only update if value is valid
      if (!value) {
        return; // Don't update if value is null/undefined
      }

      // Extract minutes from SearchBarSingle value format
      // SearchBarSingle returns an object with { value: minutesFromMidnight, displayLabel, _id, key }
      const minutes = value?.value;
      if (minutes == null || Number.isNaN(minutes)) {
        return; // Don't update if minutes is invalid
      }

      const newShifts = { ...currentWeekShifts };

      if (newShifts[dayIndex]) {
        const nextShift = {
          ...newShifts[dayIndex],
          [timeType]: minutes,
        };
        newShifts[dayIndex] = nextShift;

        setCurrentWeekShifts(newShifts);
      }
    },
    [currentWeekShifts, setCurrentWeekShifts]
  );

  // Handle openings change
  const handleOpeningsChange = useCallback(
    (dayIndex, newValue) => {
      const numValue = parseInt(newValue, 10);
      if (Number.isNaN(numValue) || numValue < 1 || numValue > 10000) return;

      const newShifts = { ...currentWeekShifts };
      if (newShifts[dayIndex]) {
        newShifts[dayIndex] = {
          ...newShifts[dayIndex],
          openings: numValue,
        };
      }
      setCurrentWeekShifts(newShifts);
    },
    [currentWeekShifts, setCurrentWeekShifts]
  );

  // Handle delete shift
  const handleDeleteShift = useCallback(
    (dayIndex) => {
      const newShifts = { ...currentWeekShifts };
      delete newShifts[dayIndex];
      setCurrentWeekShifts(newShifts);
    },
    [currentWeekShifts, setCurrentWeekShifts]
  );

  const validateRecurringTwoWeeksSelector = useCallback((_, allValues) => {
    const week1 = allValues?.week1Shifts || {};
    const week2 = allValues?.week2Shifts || {};

    const selectedShifts = [...Object.values(week1), ...Object.values(week2)].filter(Boolean);

    if (selectedShifts.length === 0) {
      return 'job.create.recurringTwoWeeks.validation.selectAtLeastOneDay';
    }

    const hasAnyMissingTimes = selectedShifts.some(
      (shift) => shift?.startTime == null || shift?.endTime == null
    );

    return hasAnyMissingTimes
      ? 'job.create.recurringTwoWeeks.validation.missingShiftTimes'
      : undefined;
  }, []);

  // Only render if position template is selected and dates are calculated
  if (!positionTemplate || !week1StartDate || !week2StartDate) {
    return null;
  }

  const styles = getStyles({ theme, activeWeek });
  const invalidShiftDaysForActiveWeek =
    activeWeek === 1 ? invalidShiftDaysWeek1 : invalidShiftDaysWeek2;

  return (
    <Box sx={{ width: '100%', ...wrapperSx }}>
      {/* Title */}
      <Typography sx={styles.titleTypographySx}>
        {LanguageConverterV2('job.create.recurringTwoWeeks.title')}
      </Typography>

      {/* Week Selector Tabs */}
      <Grid container sx={styles.weekTabsContainerSx}>
        <Grid
          data-testid="recurringTwoWeeks-weekTab-week1"
          onClick={() => setActiveWeek(1)}
          xs={6}
          item
          sx={styles.week1TabSx}
        >
          <Typography sx={styles.week1TabTypographySx}>
            {LanguageConverterV2('job.create.recurringTwoWeeks.confirm.weekLabel', {
              weekNumber: 1,
            })}
          </Typography>
        </Grid>
        <Grid
          data-testid="recurringTwoWeeks-weekTab-week2"
          onClick={() => setActiveWeek(2)}
          xs={6}
          item
          sx={styles.week2TabSx}
        >
          <Typography sx={styles.week2TabTypographySx}>
            {LanguageConverterV2('job.create.recurringTwoWeeks.confirm.weekLabel', {
              weekNumber: 2,
            })}
          </Typography>
        </Grid>
      </Grid>

      {/* Instructions */}
      <Typography sx={styles.instructionsTypographySx}>
        {LanguageConverterV2('job.create.recurringTwoWeeks.instructions')}
      </Typography>

      {/* Day Selection using CadenceField */}
      <Box sx={styles.daySelectionWrapperSx}>
        <CadenceField
          data={DAYS_OF_WEEK_DATA}
          input={{
            value: daysOfWeekValue,
            onChange: handleDaysOfWeekChange,
          }}
          meta={{}}
          testId={`recurringTwoWeeks-cadence-week${activeWeek}`}
          shouldErrorColor={false}
        />
      </Box>

      {/* Divider */}
      <Box sx={styles.dividerSx} />

      {/* Shifts Table */}
      {shiftsList.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.tableHeaderCellSx}>Day</TableCell>
                <TableCell sx={styles.tableHeaderCellSx}>Shift Time</TableCell>
                <TableCell sx={styles.tableHeaderCellSx}>Openings</TableCell>
                <TableCell sx={styles.tableHeaderWidthCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {shiftsList.map((shift) => (
                <TableRow key={shift.dayIndex}>
                  <TableCell sx={styles.dayNameCellSx}>{shift.dayName}</TableCell>
                  <TableCell>
                    <Box sx={styles.shiftTimeWrapperSx}>
                      <Box sx={styles.iconWrapperSx}>
                        <SearchBarSingle
                          testId={`startTime-week${activeWeek}-day${shift.dayIndex}`}
                          dropDownOptions={getAllValidShiftTimes()}
                          data={null}
                          name={`startTime-week${activeWeek}-day${shift.dayIndex}`}
                          variant="standard"
                          optionLabel="displayLabel"
                          placeholder={LanguageConverterV2('job.create.startTime.placeholder')}
                          optionDisplayField="displayLabel"
                          EndIcon={AccessTimeIcon}
                          customIconColor={BLACK[80]}
                          input={{
                            value:
                              shift.startTime != null
                                ? getAllValidShiftTimes().find(
                                    (time) => time.value === shift.startTime
                                  ) || null
                                : null,
                            onChange: (newValue) =>
                              handleTimeChange(shift.dayIndex, 'startTime', newValue),
                          }}
                          meta={{}}
                          shouldErrorColor={invalidShiftDaysForActiveWeek.has(shift.dayIndex)}
                          sx={styles.timeSelectorSx}
                        />
                      </Box>
                      <Typography sx={styles.separatorTypographySx}>-</Typography>
                      <Box sx={styles.iconWrapperSx}>
                        <SearchBarSingle
                          testId={`endTime-week${activeWeek}-day${shift.dayIndex}`}
                          dropDownOptions={getAllValidShiftTimes()}
                          data={null}
                          name={`endTime-week${activeWeek}-day${shift.dayIndex}`}
                          variant="standard"
                          optionLabel="displayLabel"
                          placeholder={LanguageConverterV2('job.create.endTime.placeholder')}
                          optionDisplayField="displayLabel"
                          EndIcon={AccessTimeIcon}
                          customIconColor={BLACK[80]}
                          input={{
                            value:
                              shift.endTime != null
                                ? getAllValidShiftTimes().find(
                                    (time) => time.value === shift.endTime
                                  ) || null
                                : null,
                            onChange: (newValue) =>
                              handleTimeChange(shift.dayIndex, 'endTime', newValue),
                          }}
                          meta={{}}
                          shouldErrorColor={invalidShiftDaysForActiveWeek.has(shift.dayIndex)}
                          sx={styles.timeSelectorSx}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={shift.openings}
                      onChange={(e) => handleOpeningsChange(shift.dayIndex, e.target.value)}
                      inputProps={{
                        min: 1,
                        max: 10000,
                        step: 1,
                      }}
                      sx={styles.openingsTextFieldSx}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteShift(shift.dayIndex)}
                      sx={styles.deleteButtonSx}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Overtime Alert */}
          {hasOvertime && (
            <StandardErrorBox
              message={LanguageConverterV2('job.create.recurringTwoWeeks.overtimeAlert')}
            />
          )}

          {/* Shift Duration Alert (0 minutes or > 20 hours) */}
          {hasInvalidShiftDuration && (
            <StandardErrorBox
              message={`${LanguageConverterV2('job.create.max.alert')}${LanguageConverterV2(
                'job.create.max.body'
              )}`}
            />
          )}

          {/* Overlap Error */}
          {overlapDetection.hasOverlap && (
            <StandardErrorBox
              message={LanguageConverterV2('job.create.recurringTwoWeeks.overlapError')}
            />
          )}

          {/* Section-level validation (used by CreateJobFormBody blur + review button) */}
          <Field
            name="recurringTwoWeeksSelector"
            validate={validateRecurringTwoWeeksSelector}
            subscription={{ error: true, touched: true, submitFailed: true }}
          >
            {({ meta }) => {
              const shouldShowError = meta?.error && (meta?.touched || meta?.submitFailed);
              if (!shouldShowError) return null;

              return <StandardErrorBox message={LanguageConverterV2(meta.error)} />;
            }}
          </Field>

          {/* Week Total */}
          <Box sx={styles.weekTotalBoxSx}>
            <Typography sx={styles.weekTotalLabelTypographySx}>
              {LanguageConverterV2('job.create.recurringTwoWeeks.weekTotal')}
            </Typography>
            <Typography sx={styles.weekTotalValueTypographySx}>
              {weekTotalOpenings} {LanguageConverterV2('job.create.recurringTwoWeeks.shiftsNeeded')}
            </Typography>
          </Box>
        </>
      ) : (
        <Typography sx={styles.noDaysSelectedTypographySx}>
          {LanguageConverterV2('job.create.recurringTwoWeeks.noDaysSelected')}
        </Typography>
      )}
    </Box>
  );
};

RecurringTwoWeeksSelector.propTypes = {
  wrapperSx: PropTypes.shape({}),
};

export default RecurringTwoWeeksSelector;
